import type { MibTheme } from '../../foundations';
import type { ElementRef } from '@angular/core';
import { MibIconName } from '../icons';

/** The themes a menu row can be drawn in. */
export type MibMenuItemTheme = Extract<MibTheme, 'danger'>;

export interface MibMenuItem {
  /** Display text rendered inside the item. */
  label: string;
  /**
   * Unique value emitted via `MibMenuComponent.itemSelected` when the user
   * activates this item.
   */
  value: string;
  /**
   * Optional icon.  Accepts any mib-icon name.
   * Rendered as `aria-hidden` decorative content to the left of the label.
   */
  icon?: MibIconName;
  /** When `true` the item is visible but cannot be activated. */
  disabled?: boolean;
  /** When `true` the item is highlighted, useful for menus representing filters. */
  selected?: boolean;
  /** Optional tooltip */
  tooltip?: string;
  /** A second line under the label — what the thing is, where it comes from. */
  subLabel?: string;
  /** Draws a cross on the row, which asks for the item to be removed rather than picked. */
  removable?: boolean;
  /** Draws a spinner in place of the icon while what the item stands for is being loaded. */
  loading?: boolean;
  /** Icon-only menu items drawn beside the row, before its cross, each emitted through `itemAction`. */
  actions?: MibMenuItemAction[];
  /** `'danger'` draws the row in the danger colour, for an action that destroys, such as deleting. */
  theme?: MibMenuItemTheme;
  /** What the screen needs to draw the row in its own `mibMenuItemContent` template; the menu never reads it. */
  data?: unknown;
}

/** A secondary action of a row, such as renaming what the row stands for. */
export interface MibMenuItemAction {
  /** The icon the control draws. */
  icon: MibIconName;
  /** The verb the control says to a screen reader, before the row's label: `Rename` reads `Rename <label>`. */
  label: string;
  /** Emitted through `MibMenuComponent.itemAction` when the control is pressed. */
  value: string;
  /** `'danger'` draws the control in the danger colour, for an action that destroys, such as deleting. */
  theme?: MibMenuItemTheme;
}

/** What `MibMenuComponent.itemAction` emits: the `value` of the row and the `value` of its action. */
export interface MibMenuItemActionEvent {
  item: string;
  action: string;
}

/** A horizontal dividing line between groups of items. */
export interface MibMenuSeparator {
  separator: true;
}

export type MibMenuEntry = MibMenuItem | MibMenuSeparator;

/** Type-guard — narrows an entry to `MibMenuSeparator`. */
export function isSeparator(entry: MibMenuEntry): entry is MibMenuSeparator {
  return 'separator' in entry && (entry as MibMenuSeparator).separator === true;
}

/**
 * What a menu panel needs from whatever opens it. Declared here rather than importing the directive
 * so the panel and its trigger do not import each other.
 */
export interface MibMenuTriggerHost {
  /** The element the panel is anchored to. */
  readonly elementRef: ElementRef<HTMLElement>;
  /** Whether the panel is currently open. */
  expanded(): boolean;
  close(): void;
  /** Puts focus back on the trigger. */
  focus(): void;
}

/**
 * Which edge of the trigger the panel lines up with. `start` puts the two left edges together,
 * `end` the two right ones — what a control sitting at the right of a bar needs, so the panel opens
 * inwards instead of off the screen.
 *
 * It is a preference, not a promise: the overlay still flips to the other edge, and above rather
 * than below, when there is no room for the choice.
 */
export type MibMenuAlign = 'start' | 'end';
